import { Navigate } from "react-router-dom";
import type { ReactNode } from "react";
import { useAppSelector } from "../store/hooks";
import { Spin } from "antd";
import { getToken } from "../lib/auth";
import { isLicenseReadOnlyRole, useLicenseReadOnly } from "../lib/licenseReadOnly";
import LicenseReadOnlyRedirect from "../components/license/LicenseReadOnlyRedirect";

export default function AuthorizedRoute({
  children,
  requiredRoleName,
  requiredPermissionName,
  requiresAccess,
}: {
  children: ReactNode;
  requiredRoleName?: string;
  requiredPermissionName?: string;
  // При архивиран ресторант route-ът е достъпен само в тестов режим (access)
  requiresAccess?: boolean;
}) {
  const roles = useAppSelector((s) => s.auth.roles);
  const permissions = useAppSelector((s) => s.auth.permissions);
  const status = useAppSelector((s) => s.auth.status);
  const access = useAppSelector((s) => s.auth.access);
  const restaurantAccesses = useAppSelector((s) => s.auth.restaurantAccesses);
  const licenseReadOnly = useLicenseReadOnly();
  const token = getToken(); // Get token from localStorage instead of Redux

  // If we have a token but roles are still loading, show loading spinner
  if (
    token &&
    (status === "loading" || (status === "idle" && roles.length === 0))
  ) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
        }}
      >
        <Spin size="large" />
      </div>
    );
  }

  if (requiredRoleName) {
    const hasRole = roles.some((role) => role.name === requiredRoleName);
    if (!hasRole) return <Navigate to="/" replace />;
  }

  if (requiredPermissionName) {
    const hasPermission = permissions.some(
      (p) => p.name === requiredPermissionName,
    );
    if (!hasPermission) return <Navigate to="/" replace />;
  }

  if (requiresAccess && restaurantAccesses && !access) {
    return <Navigate to="/" replace />;
  }

  // Режим „само преглед" (lib/licenseReadOnly.ts): отварят се само модулите
  // „Справки" и „Склад"; маршрут само с право (без роля) също не е от тях.
  if (licenseReadOnly !== null && !isLicenseReadOnlyRole(requiredRoleName)) {
    return <LicenseReadOnlyRedirect to="/" />;
  }

  return <>{children}</>;
}
